import { Check, Copy } from 'lucide-react';
import { memo, useEffect, useState } from 'react';
import { ensureLanguage, getHighlighter, normalizeLang } from '../lib/highlighter';

type Props = {
  code: string;
  lang?: string | null;
};

/**
 * Fenced code block. Renders shiki-highlighted HTML once the
 * highlighter is ready; falls back to plain `<pre>` in the
 * meantime so first paint doesn't wait on bundle init.
 *
 * During streaming `code` grows by 3-8 tokens every ~50ms and
 * re-highlighting on each delta blocks the main thread hard on
 * a 300-line fence. The debounced effect waits for the code to
 * stop changing for 200ms before calling shiki — natural pauses
 * between tool calls / thinking segments trigger the highlight,
 * and the final paint is always full-colour.
 */
export const CodeBlock = memo(function CodeBlock({ code, lang }: Props) {
  const cleaned = code.replace(/\n$/, '');
  const displayLang = normalizeLang(lang);
  const [html, setHtml] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        await ensureLanguage(displayLang);
        const hl = await getHighlighter();
        const out = hl.codeToHtml(cleaned, {
          lang: displayLang,
          themes: { light: 'github-light', dark: 'github-dark' },
        });
        if (!cancelled) setHtml(out);
      } catch {
        // Unsupported language or init error — keep the plain fallback.
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cleaned, displayLang]);

  async function onCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(cleaned);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — silently ignore; rare in our single-origin context.
    }
  }

  return (
    <div className="my-3 overflow-hidden rounded-xl border border-border bg-subtle">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5 text-[12px] text-muted-foreground">
        <span className="font-mono lowercase">{lang?.split(/\s/)[0] || 'text'}</span>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] hover:bg-bubble"
          title="Копировать"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? 'Скопировано' : 'Копировать'}
        </button>
      </div>
      {html ? (
        <ShikiHtml html={html} />
      ) : (
        <pre className="overflow-x-auto px-4 py-3 font-mono text-[13px] leading-relaxed">
          <code>{cleaned}</code>
        </pre>
      )}
    </div>
  );
});

function ShikiHtml({ html }: { html: string }) {
  // biome-ignore lint/security/noDangerouslySetInnerHtml: Shiki output
  return <div className="mc-code" dangerouslySetInnerHTML={{ __html: html }} />;
}
